import React, { useState, useEffect, useMemo, useCallback } from 'react';

const FillInTheBlankQuestion = ({
  question,
  answers,
  onAnswerChange,
  questionIndex
}) => {
  // Local state for immediate input responsiveness
  const [localAnswers, setLocalAnswers] = useState({});

  // Initialize local answers from props only when component mounts
  useEffect(() => {
    if (answers[question.id]) {
      setLocalAnswers(answers[question.id]);
    } else {
      // If no answers exist for this question, clear local state
      setLocalAnswers({});
    }
  }, []); // Empty dependency array - only run once on mount



  const handleBlankChange = (blankIndex, value) => {
    // Update local state immediately
    setLocalAnswers(prev => ({
      ...prev,
      [blankIndex]: value
    }));
    
    // Save to parent state immediately so answers persist
    const currentAnswers = answers[question.id] || {};
    const newAnswers = {
      ...currentAnswers,
      [blankIndex]: value
    };
    onAnswerChange(question.id, newAnswers);
  };

  const getBlankValue = (blankIndex) => {
    // Use local state first for immediate responsiveness
    if (localAnswers[blankIndex] !== undefined) {
      return localAnswers[blankIndex];
    }
    
    // Fall back to parent state for persistence
    const currentAnswers = answers[question.id] || {};
    return currentAnswers[blankIndex] || '';
  };




  // Parse the question text to identify blanks
  const parseQuestionText = (text) => {
    if (!text) return [];
    
    const parts = text.split(/\(\s*\)/g);
    const result = [];
    
    if (parts.length > 1) {
      for (let i = 0; i < parts.length - 1; i++) {
        result.push({
          id: i,
          beforeText: parts[i],
          afterText: parts[i + 1]
        });
      }
    }
    
    return result;
  };

  const blanks = parseQuestionText(question.text);

  const hasOptions = question.options && question.options.length > 0;
  const isSimpleFormat = blanks.length > 0 && !question.formula && !question.task && !question.statements;

  // Function to render question text with input fields for parentheses
  const renderQuestionWithInputs = () => {
    if (!question.text) return <span>{question.text}</span>;
    
    if (blanks.length === 0) {
      return <span>{question.text}</span>;
    }
    
    return (
      <>
        {blanks.map((blank, index) => (
          <span key={`blank-${question.id}-${index}`}>
            {blank.beforeText}
            <input
              type="text"
              value={localAnswers[index] || ''}
              onChange={(e) => handleBlankChange(index, e.target.value)}
              placeholder={`Blank ${index + 1}`}
              className="mx-2 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm text-center bg-white transition-all duration-200 w-24"
              autoComplete="off"
            />
            {index === blanks.length - 1 && blank.afterText}
          </span>
        ))}
      </>
    );
  };


  


  return (
    <div className="fill-in-the-blank-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">



      {/* Question Text */}
      <div className="mb-8">
        <div className="text-black text-lg leading-relaxed font-bold">
          {renderQuestionWithInputs()}
        </div>
      </div>

      {/* Formula Section */}
      {question.formula && (
        <div className="mb-8">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            <div className="grid grid-cols-2 divide-x divide-gray-200">
              <div className="p-5 bg-gradient-to-r from-gray-50 to-gray-100">
                <span className="text-gray-700 font-semibold text-lg">{question.formula.label}</span>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <span className="text-black text-lg font-medium">
                    {question.formula.value}
                  </span>
                  {question.formula.options && (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Description */}
      {question.task && (
        <div className="mb-8">
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {question.task.title}
              </h3>
              <span className="text-sm font-semibold text-gray-600 bg-gray-50 px-4 py-2 rounded-full border border-gray-200">
                {question.task.marks} marks
              </span>
            </div>
            <p className="text-black text-base leading-relaxed">
              {question.task.description}
            </p>
          </div>
        </div>
      )}



      {/* Complex Format: Statements to Complete */}
      {question.statements && question.statements.length > 0 && (
        <div className="mb-8">
          <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
            <h4 className="text-xl font-bold text-black mb-6 flex items-center gap-2">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Complete the following statements:
            </h4>
            
            <div className="space-y-6">
              {question.statements.map((statement, statementIndex) => (
                <div key={statementIndex} className="bg-gradient-to-r from-gray-50 to-white p-5 rounded-lg border border-gray-200 shadow-sm">
                  <div className="text-black text-base leading-relaxed">
                    {statement.parts.map((part, partIndex) => (
                      <span key={partIndex}>
                        {part.type === 'text' && part.content}
                        {part.type === 'dropdown' && (
                          <select
                            value={getBlankValue(`${statementIndex}-${partIndex}`)}
                            onChange={(e) => handleBlankChange(`${statementIndex}-${partIndex}`, e.target.value)}
                            className="mx-3 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-base bg-white shadow-sm"
                          >
                            <option value="">Select...</option>
                            {part.options.map((option, optionIndex) => (
                              <option key={optionIndex} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        )}
                        {part.type === 'input' && (
                          <input
                            type="text"
                            value={getBlankValue(`${statementIndex}-${partIndex}`)}
                            onChange={(e) => handleBlankChange(`${statementIndex}-${partIndex}`, e.target.value)}
                            placeholder="Enter text..."
                            className="mx-2 px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm w-40 bg-white"
                          />
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Selection Status */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-black font-medium">
                {isSimpleFormat ? 'Filled blanks:' : 'Completed statements:'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-600">
                {isSimpleFormat ? 
                  `${Object.keys(answers[question.id] || {}).filter(key => answers[question.id][key]?.trim()).length}`
                  : question.statements ? 
                    `${Object.keys(answers[question.id] || {}).filter(key => answers[question.id][key]?.trim()).length}`
                    : '0'
                }
              </span>
              <span className="text-gray-600 font-medium">/</span>
              <span className="text-xl font-semibold text-black">
                {isSimpleFormat ? 
                  `${blanks.length}`
                  : question.statements ? 
                    `${question.statements.reduce((total, stmt) => total + stmt.parts.filter(p => p.type !== 'text').length, 0)}`
                    : '0'
                }
              </span>
            </div>
          </div>
        </div>
      </div>



      {/* Debug Info Panel */}
      {/* Removed debug panel for production UI */}
    </div>
  );
};

export default FillInTheBlankQuestion;

